/**
 * Consolidated Shopify Integration Module
 * Handles both Storefront and Admin API interactions
 */

import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import axios from 'axios';
import type { TransformedProduct } from './shopify.types';

// Validate environment variables
const validateEnv = () => {
  const required = {
    'Store Domain': process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN,
    'Storefront Token': process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
  };

  const missing = Object.entries(required)
    .filter(([_, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new Error(`Missing required Shopify credentials: ${missing.join(', ')}`);
  }
};

validateEnv();

// Clean domain - remove any https:// or trailing slashes
const storeDomain = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN!.replace('https://', '').replace('http://', '').replace(/\/$/, '');

// Initialize Storefront API Client
export const storefrontClient = createStorefrontApiClient({
  storeDomain: process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN || '',
  apiVersion: '2025-01',
  publicAccessToken: process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN || '',
});

// Initialize Admin API Client (for admin features like order management)
const adminClient = axios.create({
  baseURL: `https://${storeDomain}/admin/api/2025-01`,
  headers: {
    'Content-Type': 'application/json',
    'X-Shopify-Access-Token': process.env.SHOPIFY_ADMIN_ACCESS_TOKEN!
  }
});

// GraphQL Query for products
const PRODUCTS_QUERY = `
  query GetProducts {
    products(first: 25, sortKey: BEST_SELLING, query: "available:true") {
      edges {
        node {
          id
          title
          handle
          description
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          featuredImage {
            url
            altText
          }
          images(first: 10) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                sku
                availableForSale
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Helper function to ensure product data is valid and consistent across environments
export function validateProduct(product: any): boolean {
  // Check if product has all required fields
  if (!product) return false;
  if (!product.id) return false;
  if (!product.title && !product.name) return false;
  if (!product.price) return false;
  
  // Ensure image is present
  if (!product.image && !product.featuredImage?.url) return false;
  
  return true;
}

// Products API (uses Storefront API)
export async function getProducts(): Promise<TransformedProduct[]> {
  try {
    validateEnv();
    
    const response = await fetch(
      `https://${process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN}/api/2023-01/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN!
        },
        body: JSON.stringify({ query: PRODUCTS_QUERY }),
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const { data } = await response.json();
    
    const products = data.products.edges.map((edge: any) => {
      const product = edge.node;
      
      // Get the first variant
      const variant = product.variants.edges[0]?.node;
      
      // Get price
      const price = variant?.price?.amount || product.priceRange.minVariantPrice.amount;
      const currencyCode = variant?.price?.currencyCode || product.priceRange.minVariantPrice.currencyCode;
      
      // Format variants
      const variants = product.variants.edges.map((variantEdge: any) => {
        const v = variantEdge.node;
        return {
          id: v.id,
          title: v.title,
          price: v.price.amount,
          currencyCode: v.price.currencyCode,
          sku: v.sku,
          availableForSale: v.availableForSale,
        };
      });
      
      // Create transformed product
      const transformedProduct = {
        id: product.id,
        title: product.title,
        handle: product.handle,
        description: product.description || '',
        price,
        currencyCode,
        image: product.featuredImage?.url || '',
        imageAlt: product.featuredImage?.altText || product.title,
        images: product.images?.edges.map((edge: any) => ({
          url: edge.node.url,
          altText: edge.node.altText || product.title
        })) || [],
        variantId: variant?.id || product.id,
        sku: variant?.sku || '',
        name: product.title,
        change: parseFloat((Math.random() * 20 - 10).toFixed(2)),
        volume: Math.floor(Math.random() * 300) + 50,
        variants: variants,
        msrp: (parseFloat(price) * 1.2).toFixed(2), // Add MSRP for display
        securityLevel: ['A+', 'A', 'B+', 'B'][Math.floor(Math.random() * 4)] // Random security level
      };
      
      // Only return products that pass validation
      if (validateProduct(transformedProduct)) {
        return transformedProduct;
      }
      return null;
    }).filter(Boolean); // Remove null products
    
    console.log(`✅ Fetched ${products.length} products`);
    return products;
    
  } catch (error) {
    console.error('❌ Error fetching products:', error);
    return [];
  }
}

// Single Product API (uses Storefront API)
export async function getProduct(handle: string): Promise<TransformedProduct | null> {
  try {
    const products = await getProducts();
    return products.find(p => p.handle === handle) || null;
  } catch (error) {
    console.error('Error fetching product:', error);
    return null;
  }
}

// Cart API (uses Storefront API)
export async function createCartWithItems(items: { id: string; quantity: number }[]) {
  const cartMutation = `
    mutation createCart($lines: [CartLineInput!]!) {
      cartCreate(input: { lines: $lines }) {
        cart {
          checkoutUrl
          id
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  try {
    const response = await storefrontClient.request(cartMutation, {
      variables: {
        lines: items.map(item => ({
          merchandiseId: item.id,
          quantity: item.quantity
        }))
      }
    });

    const cart = response.data?.cartCreate?.cart;
    if (!cart) {
      throw new Error('Failed to create cart');
    }

    return cart;
  } catch (error) {
    console.error('Error creating cart:', error);
    throw error;
  }
}

// Orders API (uses Admin API)
export async function getOrders() {
  try {
    console.log('🔄 Fetching orders from Admin API...');
    
    const response = await adminClient.get('/orders.json', {
      params: {
        limit: 50,
        status: 'any',
        fields: [
          'id',
          'name',
          'created_at',
          'fulfillment_status',
          'fulfillments',
          'shipping_address',
          'line_items',
          'tags',
          'shipping_lines'
        ].join(',')
      }
    });

    // Add type assertion for the response data
    const responseData = response.data as { orders?: any[] };
    console.log(`✅ Fetched ${responseData.orders?.length || 0} orders`);
    return responseData.orders || [];
  } catch (error) {
    console.error('❌ Error fetching orders:', error);
    throw error;
  }
}

// Shop Status API (uses Admin API)
export async function getShopStatus(): Promise<boolean> {
  try {
    const response = await adminClient.get('/shop.json');
    // Add type assertion for the response data
    const shopData = response.data as { shop?: { id?: string } };
    return !!shopData.shop?.id;
  } catch (error) {
    console.error('Error checking shop status:', error);
    return false;
  }
} 